import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";

// Reuse the same session-only token storage as AdminSubscribers.
const TOKEN_KEY = "twtg_admin_token";

export default function AdminDownloads() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async (t) => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/admin/downloads", { params: { token: t } });
      setData(data);
      sessionStorage.setItem(TOKEN_KEY, t);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load");
      setData(null);
      sessionStorage.removeItem(TOKEN_KEY);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem(TOKEN_KEY);
    if (saved) load(saved);
  }, [load]);

  const exportCsv = () => {
    if (!data?.rows?.length) return;
    const rows = [["slug", "title", "destination", "published", "downloads"]].concat(
      data.rows.map(r => [r.slug, r.title, r.destination, r.published, String(r.count)])
    );
    const csv = rows.map(r => r.map(c => `"${(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `downloads-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <main data-testid="admin-downloads-page" className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
      <div className="overline text-olive mb-4">Admin</div>
      <h1 className="font-serif text-5xl md:text-6xl mb-10">Downloads</h1>

      <div className="flex items-end gap-3 mb-10 max-w-xl">
        <label className="flex-1 flex flex-col gap-2">
          <span className="overline text-muted">Admin token</span>
          <input
            type="password"
            value={token}
            onChange={e => setToken(e.target.value)}
            className="border border-line bg-surface px-4 py-3 focus:border-olive outline-none"
            placeholder="Paste your ADMIN_TOKEN…"
            data-testid="admin-token-input"
          />
        </label>
        <button onClick={() => load(token)} disabled={!token || loading} className="btn-primary" data-testid="admin-load-btn">
          {loading ? "…" : "Load"}
        </button>
      </div>

      {error && <div className="text-clay border border-clay px-5 py-3 mb-6">{error}</div>}

      {data && (
        <>
          <div className="flex items-center justify-between mb-6">
            <div className="overline text-muted" data-testid="admin-downloads-total">
              {data.total.toLocaleString()} total downloads · {data.rows.length} articles
            </div>
            <button onClick={exportCsv} className="btn-outline" data-testid="admin-export-csv">Export CSV</button>
          </div>
          <div className="border border-line">
            <div className="grid grid-cols-12 px-5 py-3 border-b border-line bg-alt overline text-muted">
              <div className="col-span-6">Article</div>
              <div className="col-span-3">Published</div>
              <div className="col-span-3 text-right">Downloads</div>
            </div>
            {data.rows.map(r => (
              <div
                key={r.slug}
                className="grid grid-cols-12 px-5 py-3 border-b border-line text-sm items-center"
                data-testid={`admin-dl-${r.slug}`}
              >
                <div className="col-span-6">
                  <a
                    href={`/articles/${r.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-ink hover:text-olive transition-colors"
                  >
                    {r.title}
                  </a>
                  {!r.has_pdf && (
                    <span className="ml-2 text-[0.65rem] uppercase tracking-[0.18em] text-muted">No PDF</span>
                  )}
                </div>
                <div className="col-span-3 text-muted">{r.published}</div>
                <div className="col-span-3 text-right font-mono tabular-nums text-ink">
                  {r.count.toLocaleString()}
                </div>
              </div>
            ))}
            {data.rows.length === 0 && (
              <div className="px-5 py-8 text-muted text-center">No articles yet.</div>
            )}
          </div>
        </>
      )}
    </main>
  );
}
